/* uk-measure.js — the one measurement module.
 *
 * THE RULE: every dimension is stored internally as MILLIMETRES, as an integer.
 * Parse on input, format on display, never store a display string. A wall entered
 * as 10 ft and the same wall entered as 3.048 m are the SAME NUMBER by the time
 * anything calculates with them, so they cannot produce different panel counts.
 *
 * Consumed by the wall visualiser, the mini calculator in the buy box and the
 * project calculator. They share this file so they cannot disagree: set ft-in in
 * one and the others follow, because the unit lives here and broadcasts a change.
 *
 * Conversions are exact by definition, not approximations:
 *   1 in = 25.4 mm      1 ft = 304.8 mm      1 cm = 10 mm      1 m = 1000 mm
 * Rounding happens at display time only. Nothing rounds mid-calculation.
 */
(function (global) {
  'use strict';

  var MM = { mm: 1, cm: 10, m: 1000, in: 25.4, ft: 304.8 };
  /* FEET, NOT FEET-AND-INCHES (Jeff, 14 Sep): "Nobody in the UK types feet and
     inches into a box; they say 'about 10 foot'." So a decimal-feet unit, and
     the two-box ft/in row goes with it — along with the type="text" special
     case it needed, because every unit is now one number in one numeric box.
     PARSING "8'6" still works and always will: someone who types it means
     2591 mm, and refusing to understand what they typed is not the same as
     not offering it as a unit. */
  var UNITS = ['m', 'cm', 'mm', 'in', 'ft'];
  /* 'ftin' was the old name for what is now 'ft'. Anyone carrying it in
     localStorage gets moved across rather than silently reset to metres. */
  var LEGACY_UNITS = { ftin: 'ft' };
  var STORE_KEY = 'uk:measure:unit';
  var DEFAULT_UNIT = 'm'; // a first-time UK visitor thinks in metres

  var M = {};

  M.UNITS = UNITS;
  M.MM_PER = MM;

  /* ------------------------------------------------------------------ parse */

  /* Accepts what people actually type:
       "2.4"  "2400"  "240cm"  "2.4 m"  "8'6"  "8' 6\""  "8 ft 6"  "8ft6in"  "96in"
     `unit` is the row's current unit and decides what a bare number means.
     Returns integer millimetres, or null when there is nothing usable. */
  M.parse = function (raw, unit) {
    if (raw === null || raw === undefined) return null;
    var s = String(raw).trim().toLowerCase();
    if (!s) return null;
    s = s.replace(/,/g, '.').replace(/′/g, "'").replace(/″|”|’’/g, '"');

    // Feet and inches, in any of the shapes people write them.
    var ftin = s.match(/^(-?\d+(?:\.\d+)?)\s*(?:'|ft|feet|foot)\s*(\d+(?:\.\d+)?)?\s*(?:"|in|inch|inches)?$/);
    if (ftin) {
      var ft = parseFloat(ftin[1]);
      var inch = ftin[2] ? parseFloat(ftin[2]) : 0;
      if (!isFinite(ft) || !isFinite(inch)) return null;
      return Math.round(ft * MM.ft + inch * MM.in);
    }

    // A number with its unit written on it wins over the row's unit.
    var withUnit = s.match(/^(-?\d+(?:\.\d+)?)\s*(mm|millimetres?|millimeters?|cm|centimetres?|centimeters?|m|metres?|meters?|in|inch|inches|"|ft|feet|foot|')$/);
    if (withUnit) {
      var n = parseFloat(withUnit[1]);
      if (!isFinite(n)) return null;
      var u = withUnit[2];
      var per = /^(mm|milli)/.test(u) ? MM.mm
        : /^(cm|centi)/.test(u) ? MM.cm
        : /^(m|metre|meter)/.test(u) ? MM.m
        : /^(in|inch|")/.test(u) ? MM.in
        : MM.ft;
      return Math.round(n * per);
    }

    // A bare number, read in the row's unit. ft-in rows read a bare number as feet.
    var bare = s.match(/^(-?\d+(?:\.\d+)?)$/);
    if (bare) {
      var v = parseFloat(bare[1]);
      if (!isFinite(v)) return null;
      var mmPer = MM[M.normUnit(unit)] || MM.m;
      return Math.round(v * mmPer);
    }
    return null;
  };

  /* Two boxes on a ft-in row. Either may be blank. */
  M.parseFeetInches = function (feet, inches) {
    var f = parseFloat(String(feet == null ? '' : feet).trim());
    var i = parseFloat(String(inches == null ? '' : inches).trim());
    if (!isFinite(f)) f = 0;
    if (!isFinite(i)) i = 0;
    if (!f && !i) return null;
    return Math.round(f * MM.ft + i * MM.in);
  };

  /* ----------------------------------------------------------------- format */

  var round = function (n, dp) {
    var f = Math.pow(10, dp);
    return (Math.round(n * f) / f).toFixed(dp);
  };

  /* mm -> the string a customer reads. Always carries its unit. */
  M.format = function (mm, unit, opts) {
    if (mm === null || mm === undefined || !isFinite(mm)) return '';
    var o = opts || {};
    switch (unit) {
      case 'mm': return round(mm, 0) + (o.bare ? '' : ' mm');
      case 'cm': return round(mm / MM.cm, 0) + (o.bare ? '' : ' cm');
      case 'in': return round(mm / MM.in, 1) + (o.bare ? '' : ' in');
      case 'ft': return round(mm / MM.ft, 1) + (o.bare ? '' : ' ft');
      /* still formats, for anything holding the old name */
      case 'ftin': {
        var totalIn = mm / MM.in;
        var ft = Math.floor(totalIn / 12);
        var inch = Math.round((totalIn - ft * 12) * 10) / 10;
        if (inch >= 12) { ft += 1; inch -= 12; }          // 11.97in must not print as 12"
        var inchTxt = (Math.round(inch * 10) % 10 === 0) ? String(Math.round(inch)) : String(round(inch, 1));
        return ft + "' " + inchTxt + '"';
      }
      default: return round(mm / MM.m, 2) + (o.bare ? '' : ' m');
    }
  };

  /* What goes back INTO the input after parsing, so the customer sees what we understood. */
  M.formatForInput = function (mm, unit) {
    if (mm === null || mm === undefined) return '';
    if (unit === 'ftin') return M.format(mm, 'ftin');
    /* feet are a plain number in a plain numeric box now, so they trim like
       every other unit: 10, not 10.0 */
    /* Trailing zeros are for display, not for a box someone is about to edit:
       a wall typed as "3", switched to cm and back, must read "3" again and not
       "3.00". format() keeps its fixed decimals; only the input is trimmed. */
    return M.format(mm, unit, { bare: true }).replace(/\.0+$|(\.\d*?)0+$/, '$1');
  };

  M.toFeetInches = function (mm) {
    var totalIn = mm / MM.in;
    var ft = Math.floor(totalIn / 12);
    var inch = Math.round((totalIn - ft * 12) * 10) / 10;
    if (inch >= 12) { ft += 1; inch -= 12; }
    return { feet: ft, inches: inch };
  };

  /* ------------------------------------------------------------------- unit */

  M.isUnit = function (u) { return UNITS.indexOf(u) !== -1; };
  /* The one place an old name becomes a current one. */
  M.normUnit = function (u) {
    if (LEGACY_UNITS[u]) return LEGACY_UNITS[u];
    return M.isUnit(u) ? u : DEFAULT_UNIT;
  };

  M.getUnit = function () {
    try {
      var u = global.localStorage && global.localStorage.getItem(STORE_KEY);
      if (u && LEGACY_UNITS[u]) return LEGACY_UNITS[u];
      if (u && M.isUnit(u)) return u;
    } catch (e) { /* private mode, blocked storage — fall through */ }
    return DEFAULT_UNIT;
  };

  /* Setting the unit tells every consumer at once, so the visualiser, the mini
     calculator and the project calculator can never drift apart. */
  M.setUnit = function (u) {
    if (LEGACY_UNITS[u]) u = LEGACY_UNITS[u];
    if (!M.isUnit(u)) return M.getUnit();
    try { global.localStorage && global.localStorage.setItem(STORE_KEY, u); } catch (e) {}
    try {
      global.dispatchEvent(new CustomEvent('uk:unitchange', { detail: { unit: u } }));
    } catch (e) {
      // Older Safari without the CustomEvent constructor.
      var ev = document.createEvent('CustomEvent');
      ev.initCustomEvent('uk:unitchange', false, false, { unit: u });
      global.dispatchEvent(ev);
    }
    return u;
  };

  M.onUnitChange = function (fn) {
    var h = function (e) { fn((e && e.detail && e.detail.unit) || M.getUnit()); };
    global.addEventListener('uk:unitchange', h);
    return function () { global.removeEventListener('uk:unitchange', h); };
  };

  M.unitLabel = function (u) {
    return { m: 'm', cm: 'cm', mm: 'mm', in: 'in', ft: 'ft', ftin: 'ft' }[u] || u;
  };

  /* ------------------------------------------------------------------- area */

  /* Panel count from millimetres. Every caller goes through this, so a wall is
     the same number of panels however its dimensions were typed. */
  M.areaM2 = function (wMm, hMm) { return (wMm / MM.m) * (hMm / MM.m); };

  M.panelsFor = function (wMm, hMm, coverageM2, wastePct) {
    if (!(coverageM2 > 0)) return 0;
    var waste = 1 + (wastePct || 0) / 100;
    return Math.ceil((M.areaM2(wMm, hMm) / coverageM2) * waste);
  };

  global.UKMeasure = M;
  if (global.UK) global.UK.measure = M;
})(typeof window !== 'undefined' ? window : globalThis);
